import React, { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, Calendar } from "lucide-react";
import Sidebar from "../../../../component/includes/sidebar";
import {
  ArticleCategory,
  articleCategoryService,
  CreateArticleCategoryData,
  UpdateArticleCategoryData,
} from "../../../../services/Artikel/Category";

const ArticleCategoryManagement = () => {
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingCategory, setEditingCategory] = useState<ArticleCategory | null>(null);
  const [newCategory, setNewCategory] = useState<CreateArticleCategoryData>({ name: "" });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await articleCategoryService.getArticleCategories();
      let categoryArray: ArticleCategory[] = [];
      if (Array.isArray(response)) {
        categoryArray = response;
      } else if (response && typeof response === 'object') {
        categoryArray =
          (response as any)?.data ??
          (response as any)?.categories ??
          Object.values(response).find((val: any) => Array.isArray(val)) ??
          [];
      }
      setCategories(Array.isArray(categoryArray) ? categoryArray : []);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to fetch article categories");
      console.error("Error fetching categories:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      setError("Category name cannot be empty");
      return;
    }

    try {
      await articleCategoryService.createArticleCategory(newCategory);
      await fetchCategories();
      setSuccessMessage("Category created successfully");
      setIsModalOpen(false);
      setNewCategory({ name: "" });
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to create category");
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;

    try {
      const updatedData: UpdateArticleCategoryData = {
        id: editingCategory.id,
        name: editingCategory.name,
      };
      await articleCategoryService.updateArticleCategory(updatedData);
      await fetchCategories();
      setSuccessMessage("Category updated successfully");
      setIsModalOpen(false);
      setEditingCategory(null);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to update category");
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;

    try {
      await articleCategoryService.deleteArticleCategory(categoryId);
      await fetchCategories();
      setSuccessMessage("Category deleted successfully");
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to delete category");
    }
  };

  // Filtered categories
  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar/>

      <div className="flex-1 flex flex-col">
<div className="flex-1 p-4 md:p-6 mt-14">
          <div className="max-w-7xl mx-auto">
            {/* Header - Mirip Gambar */}
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center">
                  <span className="text-white text-3xl">📋</span>
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">Article Category Management</h1>
                  <p className="text-gray-500 text-sm mt-1">Manage your article categories</p>
                </div>
              </div>

              <button
                onClick={() => {
                  setIsModalOpen(true);
                  setEditingCategory(null);
                  setNewCategory({ name: "" });
                }}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-2xl font-medium transition-all active:scale-95"
              >
                <Plus className="w-5 h-5" />
                Add New Category
              </button>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search categories by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-2xl py-4 pl-12 pr-6 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400 transition-all"
                />
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">🔍</div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-24">NUMBER</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">NAME</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-44">CREATED AT</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-44">UPDATED AT</th>
                    <th className="px-8 py-5 text-right font-medium text-gray-600 w-32">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCategories.map((category, index) => (
                    <tr key={category.id} className="hover:bg-gray-50 transition-colors">
                      {/* NUMBER (Nomor Urut) */}
                      <td className="px-8 py-6">
                        <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-lg">
                          {index + 1}
                        </div>
                      </td>

                      {/* Name */}
                      <td className="px-8 py-6 font-medium text-gray-800">
                        {category.name}
                      </td>

                      {/* Created At */}
                      <td className="px-8 py-6 text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(category.created_at).toLocaleDateString('id-ID', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </td>

                      {/* Updated At */}
                      <td className="px-8 py-6 text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(category.updated_at).toLocaleDateString('id-ID', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-8 py-6">
                        <div className="flex items-center justify-end gap-4">
                          <button
                            onClick={() => {
                              setEditingCategory(category);
                              setIsModalOpen(true);
                            }}
                            className="p-3 text-blue-500 hover:bg-blue-50 rounded-2xl transition-all hover:scale-110"
                            title="Edit"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category.id)}
                            className="p-3 text-red-500 hover:bg-red-50 rounded-2xl transition-all hover:scale-110"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {filteredCategories.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-20 text-center text-gray-400">
                        No categories found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Add/Edit */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-md">
            <h2 className="text-2xl font-semibold mb-6">
              {editingCategory ? "Edit Category" : "Add New Category"}
            </h2>

            <input
              type="text"
              value={editingCategory ? editingCategory.name : newCategory.name}
              onChange={(e) => {
                if (editingCategory) {
                  setEditingCategory({ ...editingCategory, name: e.target.value });
                } else {
                  setNewCategory({ name: e.target.value });
                }
              }}
              placeholder="Enter category name..."
              className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-purple-500"
            />

            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 py-4 border border-gray-300 rounded-2xl font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}
                className="flex-1 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-medium transition-all"
              >
                {editingCategory ? "Update Category" : "Create Category"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Notification */}
      {successMessage && (
        <div className="fixed bottom-6 right-6 bg-green-500 text-white px-6 py-4 rounded-2xl shadow-xl z-50">
          ✅ {successMessage}
        </div>
      )}

      {/* Error Notification */}
      {error && (
        <div className="fixed bottom-6 right-6 bg-red-500 text-white px-6 py-4 rounded-2xl shadow-xl z-50">
          {error}
        </div>
      )}
    </div>
  );
};

export default ArticleCategoryManagement;